import { z } from "zod";

/**
 * The tenant log pipeline's health contract: what "logs are flowing" means, and
 * the one function that decides it.
 *
 * ## Why this exists
 *
 * A tenant's log store can be up, `/ready`, reachable, and EMPTY FOREVER — that
 * was the state of every tenant until the stdout shipper existed, and nothing
 * reported it, because an empty page and a dead pipeline are the same bytes. So
 * health is not "Loki answers". It is "a line written on every node that runs
 * apps was readable back out of Loki recently".
 *
 * ## The canary
 *
 * Every node that hosts an app runs one shipper allocation — a Nomad job PER
 * NODE, `vibe-logship-<nodeId>`, pinned to that node, for exactly as long as
 * the node hosts one (`log-shipping-nodes.ts` says why no longer). The shipper tails its OWN
 * stderr out of its own allocation log directory — the same logmon-written file
 * shape an app's stdout lands in — and a periodic heartbeat guarantees that
 * stream is never quiet. The lines travel the exact path an app's lines take:
 * logmon file → filelog on the node → OTLP to the tenant collector → Loki. So
 * "this node's canary is readable in Loki" proves that node's path end to end,
 * and a node whose canary is missing is a node whose apps' logs are not
 * arriving either.
 *
 * ## Two readers, one verdict
 *
 * The agent assesses on every reconcile pass and reports it (the continuous
 * alarm); `vibe-log-gateway` assesses on every read (so the CLI can say
 * "pipeline broken" instead of printing nothing). Both call
 * {@link assessLogPipeline}, so the two can never disagree about what healthy
 * means — only about the instant they looked.
 */

/**
 * The prefix of every shipper job. One job PER NODE, never a system job: a
 * shipper may mount ONLY the log directories of the allocations on its own node
 * — each allocation's `secrets/` sits beside its logs, `0777` with `0644`
 * files, readable by any uid — and a system job's single spec cannot name
 * per-node mounts. See `vibeLogshipJobName`.
 *
 * Single-sourced here because three programs address it: the agent builds and
 * submits the jobs, and both the agent and the gateway enumerate their
 * allocations to learn which nodes have a running shipper.
 */
export const VIBE_LOGSHIP_JOB_PREFIX = "vibe-logship-";

/** The shipper job for one node. The node id is Nomad's, so it is already unique and DNS-safe. */
export function vibeLogshipJobName(nodeId: string): string {
  return `${VIBE_LOGSHIP_JOB_PREFIX}${nodeId}`;
}

/**
 * The task name inside a shipper job. Nomad's logmon names the task's log
 * files after it (`<task>.stderr.<n>`), and the shipper's canary receiver tails
 * exactly that file — so the jobspec and the collector config must agree on it.
 */
export const VIBE_LOGSHIP_TASK_NAME = "shipper";

/**
 * The node pool whose nodes are expected to report — Nomad's `default`, where
 * every app is placed: an app jobspec names no pool, and the git host's and
 * telemetry node's dedicated pools are what keep apps (and so shippers) off
 * them once those nodes have rolled.
 */
export const VIBE_LOGSHIP_NODE_POOL = "default";

/**
 * The `nexus.app.id` the canary stream carries — and therefore the value of the
 * `nexus_app_id` Loki index label it lands under.
 *
 * Deliberately not a UUID: every real app id is one, and the control plane's
 * logs route refuses anything else, so no customer read can select this stream
 * by accident and no app can ever collide with it.
 */
export const VIBE_LOG_CANARY_APP_ID = "nexus-log-canary";

/**
 * The resource attribute naming the node a canary line came from. Structured
 * metadata, never an index label: node ids churn with the ASG, and the canary
 * needs to be ONE stream per tenant, not one per node that ever existed.
 */
export const VIBE_LOG_CANARY_NODE_ATTRIBUTE = "nexus.node.id";

/**
 * The same attribute as Loki returns it on a canary line — in the stream's
 * label set on a default range query, in the entry's structured metadata on a
 * categorised one (see `canaryReportingNodeIds`).
 *
 * 🔴 UNDERSCORED, AND THE DOTTED FORM MATCHES NOTHING. Loki normalises OTLP
 * attribute names — dots become underscores — for structured metadata exactly as
 * it does for index labels (`nexus.app.id` → `nexus_app_id`). Reading the
 * dotted key off a returned entry yields undefined for every line, which would
 * report every node silent on a perfectly healthy pipeline.
 */
export const VIBE_LOG_CANARY_NODE_METADATA_KEY = "nexus_node_id";

/**
 * Which of a task's two streams a shipped line came from, as a log attribute
 * (structured metadata in Loki). Named per the OTel log semantic conventions.
 */
export const VIBE_LOG_IOSTREAM_ATTRIBUTE = "log.iostream";

/**
 * How far back a canary line still counts as proof the node's path works.
 *
 * Twenty heartbeats at {@link VIBE_LOG_CANARY_HEARTBEAT_SECONDS}: long enough
 * that a collector restart, a Loki flush or a slow batch never reads as an
 * outage, short enough that an operator learns of a real one within one
 * coffee. Also the grace a NEW shipper gets before its silence counts.
 */
export const VIBE_LOG_CANARY_WINDOW_MS = 10 * 60 * 1000;

/** How often every shipper guarantees at least one canary line. */
export const VIBE_LOG_CANARY_HEARTBEAT_SECONDS = 30;

/**
 * The reasons a verdict can carry. A closed set, so an alert rule, the CLI and a
 * dashboard can each switch on it instead of parsing `detail`.
 */
export const VIBE_LOG_PIPELINE_REASONS = [
  /** Every expected node's canary was read back inside the window. */
  "all_nodes_reporting",
  /** No ready, eligible node hosts an app — nothing to vouch for either way. */
  "no_app_nodes",
  /** No shipper allocation is running at all — nothing can ship any line. */
  "no_shipper_running",
  /** Every shipper is younger than the window and none has reported yet. */
  "shippers_starting",
  /** Some nodes' canaries are missing; the rest are flowing. */
  "nodes_silent",
  /** Not one canary line from any node inside the window: zero ingest. */
  "no_canary",
  /** The check itself could not run (Nomad or Loki did not answer). */
  "check_failed"
] as const;

export const VibeLogPipelineReasonSchema = z.enum(VIBE_LOG_PIPELINE_REASONS);
export type VibeLogPipelineReason = z.infer<typeof VibeLogPipelineReasonSchema>;

/**
 * `unverified` is NOT a softer `healthy`. It means nobody could look, and an
 * empty result under it is exactly as untrustworthy as under `unhealthy` — the
 * CLI treats the two the same for an empty read.
 */
export const VIBE_LOG_PIPELINE_STATUSES = ["healthy", "unhealthy", "unverified"] as const;
export const VibeLogPipelineStatusSchema = z.enum(VIBE_LOG_PIPELINE_STATUSES);
export type VibeLogPipelineStatus = z.infer<typeof VibeLogPipelineStatusSchema>;

export const VibeLogPipelineHealthSchema = z.object({
  status: VibeLogPipelineStatusSchema,
  reason: VibeLogPipelineReasonSchema,
  /** One sentence, in the platform's voice, naming what was measured. */
  detail: z.string().max(2000),
  /** Ready, eligible nodes hosting an app — every one MUST report. */
  expectedNodes: z.number().int().nonnegative(),
  /** Of those, the nodes whose canary was read back inside the window. */
  reportingNodes: z.number().int().nonnegative(),
  /**
   * Nodes whose canary is missing and whose shipper is past its warm-up, or
   * has no shipper at all. Empty when healthy.
   */
  silentNodes: z.array(z.string()),
  /** When the check ran, ISO 8601. */
  checkedAt: z.string()
});
export type VibeLogPipelineHealth = z.infer<typeof VibeLogPipelineHealthSchema>;
